import { appBarMeta } from "@constants/appbar";
import { PlaySavedPodcastsButton } from "@features/podcasts/components/PlaySavedPodcastsButton";
import { PodcastListItem } from "@features/podcasts/components/PodcastListItem";
import { usePodcasts } from "@features/podcasts/hooks/usePodcasts";
import { useSavedPodcasts } from "@features/podcasts/hooks/useSavedPodcasts";
import type { Document } from "@features/podcasts/types";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/src/components/Box";
import Container from "@memorang/ui/src/components/Container";
import EmptyState from "@memorang/ui/src/components/EmptyView";
import Loader from "@memorang/ui/src/components/Loader";
import CustomAppbar from "components/CustomAppbar";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import CustomTabs from "components/CustomTabs";
import DocumentTitle from "components/DocumentTitle";
import { useTranslation } from "react-i18next";
import { Dimensions, SectionList } from "react-native";
import { Card, Divider } from "react-native-paper";
import EmptyStateBookmark from "../../../assets/empty_state_bookmark.svg";

const AllPodcastsTab = () => {
	const { podcastsByType, isLoading, podcasts } = usePodcasts();
	const theme = useAppTheme();
	const sections = podcastsByType.map(({ type, podcasts }) => ({
		title: type,
		data: podcasts,
	}));

	const renderItem = ({ item }: { item: Document }) => {
		return <PodcastListItem podcast={item} />;
	};

	const renderSectionHeader = ({
		section: { title },
	}: {
		section: { title: string };
	}) => (
		<CustomListSubheaderContainer
			style={{
				paddingTop: 16,
				paddingBottom: 16,
				backgroundColor: theme.colors.elevation.level1,
			}}
		>
			{title}
		</CustomListSubheaderContainer>
	);

	if (isLoading) {
		return <Loader />;
	}

	return (
		<Box flexGrow={1} height="90%">
			<Card.Title
				title={`${podcasts.length} Podcasts`}
				titleVariant="titleSmall"
				titleStyle={{
					paddingVertical: 12,
				}}
			/>
			<Divider />
			<SectionList
				sections={sections}
				keyExtractor={(item) => item.id.toString()}
				renderItem={renderItem}
				renderSectionHeader={renderSectionHeader}
				showsVerticalScrollIndicator={isWeb}
				style={{ flexGrow: 1 }}
			/>
		</Box>
	);
};

const SavedPodcastsTab = () => {
	const { savedPodcasts, savedPodcastsByType, isLoading } = useSavedPodcasts();

	const sections = savedPodcastsByType.map(({ type, podcasts }) => ({
		title: type,
		data: podcasts,
	}));

	const renderItem = ({ item }: { item: Document }) => {
		return <PodcastListItem podcast={item} />;
	};

	const renderSectionHeader = ({
		section: { title },
	}: {
		section: { title: string };
	}) => (
		<CustomListSubheaderContainer
			style={{
				paddingTop: 16,
				paddingBottom: 16,
			}}
		>
			{title}
		</CustomListSubheaderContainer>
	);

	if (isLoading) {
		return <Loader />;
	}

	if (savedPodcasts.length === 0) {
		return (
			<EmptyState
				title="No saved podcasts"
				subtitle="Save your favorite podcasts to find them here"
				image={EmptyStateBookmark}
			/>
		);
	}

	return (
		<Box flexGrow={1} height="100%">
			<Card.Title
				title={`${savedPodcasts.length} Saved Podcasts`}
				titleVariant="titleSmall"
				titleStyle={{
					paddingVertical: 12,
				}}
			/>
			<Divider />
			<Box padding={16}>
				<PlaySavedPodcastsButton />
			</Box>
			<SectionList
				sections={sections}
				keyExtractor={(item) => item.id.toString()}
				renderItem={renderItem}
				renderSectionHeader={renderSectionHeader}
				showsVerticalScrollIndicator={false}
				style={{ flexGrow: 1 }}
			/>
		</Box>
	);
};

const Podcasts = () => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	return (
		<>
			<CustomAppbar
				showCommandBar={isWeb}
				showHambugerMenu
				options={{
					headerTitle: t(appBarMeta.podcasts.title),
					subtitle: isMobile ? "" : t(appBarMeta.podcasts.description),
				}}
			/>
			<DocumentTitle title={t(appBarMeta.podcasts.title)} />
			<Container
				style={{
					paddingVertical: isMobile ? 0 : 32,
					paddingHorizontal: isMobile ? 0 : 32,
				}}
			>
				<Card
					mode="contained"
					style={{
						backgroundColor: theme.colors.elevation.level1,
						borderRadius: 12,
					}}
				>
					<Box
						flexGrow={1}
						height="100%"
						justifyContent="space-between"
						maxHeight={
							isWeb ? Dimensions.get("window").height - 200 : undefined
						}
						minHeight={
							isWeb ? Dimensions.get("window").height - 200 : undefined
						}
					>
						<CustomTabs
							mode="fixed"
							// backgroundColor="transparent"
							tabs={[
								{
									label: "All Podcasts",
									content: <AllPodcastsTab />,
								},
								{
									label: "Saved",
									content: <SavedPodcastsTab />,
								},
							]}
						/>
					</Box>
				</Card>
			</Container>
		</>
	);
};

export default Podcasts;
